import React, { useState } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { useApiService } from '../services/useApiService';
import LoadingSpinner from '../components/LoadingSpinner';
import NaibLogo from '../components/NaibLogo';
import { KeyIcon } from '@heroicons/react/24/outline';
import { mapPublicAuthError } from '../utils/authErrorMessage';

function ResetPassword() {
  const { token: tokenParam } = useParams();
  const location = useLocation();
  const token = tokenParam || new URLSearchParams(location.search).get('token');
  const navigate = useNavigate();
  const { auth } = useApiService();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (!token) {
      setError('This reset link is missing information. Request a new password reset from the login page.');
      return;
    }

    setLoading(true);

    try {
      await auth.resetPassword(token, formData.password);
      setSuccess(true);
      setTimeout(() => {
        navigate('/login', {
          state: { message: 'Password has been reset successfully. Please login with your new password.' }
        });
      }, 2000);
    } catch (error) {
      const apiMessage = error.response?.data?.error;
      setError(
        apiMessage && !/status code|internal server/i.test(apiMessage)
          ? apiMessage
          : mapPublicAuthError(error, { actionLabel: 'password reset attempts' })
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-6 sm:px-6 lg:px-6">
      <div className="max-w-md w-full p-8 md:p-10 2xl:p-12 3xl:p-14 bg-white rounded-2xl shadow-xl">
        <div className="pb-4">
          <NaibLogo logoClassName="h-9 w-9" nameClassName="h-7 w-auto max-w-[180px]" alt="Onahiri" />
        </div>
        
        <div className="text-sm font-light text-gray-500 pb-8">
          Enter your new password
        </div>

        <form className="flex flex-col" onSubmit={handleSubmit}>
          {error && (
            <div className="rounded-md bg-red-50 p-4 mb-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          {success && (
            <div className="rounded-md bg-green-50 p-4 mb-4">
              <div className="text-sm text-green-700">
                Password reset successful! Redirecting to login...
              </div>
            </div>
          )}

          <div className="pb-2">
            <label htmlFor="password" className="block mb-2 text-sm font-medium text-gray-900">
              New Password
            </label>
            <div className="relative text-gray-400">
              <span className="absolute inset-y-0 left-0 flex items-center p-1 pl-3">
                <KeyIcon className="h-5 w-5" />
              </span>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="pl-12 mb-2 bg-gray-50 text-gray-600 border focus:border-transparent border-gray-300 sm:text-sm rounded-lg ring ring-transparent focus:ring-1 focus:outline-none focus:ring-gray-400 block w-full p-2.5 py-3 px-4"
                placeholder="••••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
            </div>
          </div>

          <div className="pb-6">
            <label htmlFor="confirmPassword" className="block mb-2 text-sm font-medium text-gray-900">
              Confirm New Password
            </label>
            <div className="relative text-gray-400">
              <span className="absolute inset-y-0 left-0 flex items-center p-1 pl-3">
                <KeyIcon className="h-5 w-5" />
              </span>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                className="pl-12 mb-2 bg-gray-50 text-gray-600 border focus:border-transparent border-gray-300 sm:text-sm rounded-lg ring ring-transparent focus:ring-1 focus:outline-none focus:ring-gray-400 block w-full p-2.5 py-3 px-4"
                placeholder="••••••••••"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || success}
            className={`w-full text-white bg-gray-900 hover:bg-gray-800 focus:ring-4 focus:outline-none focus:ring-gray-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center transition-colors duration-200 ${
              (loading || success) ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>

          <div className="mt-4 text-center">
            <Link
              to="/login"
              className="text-sm font-medium text-gray-900 hover:text-gray-700"
            >
              Back to login
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ResetPassword; 